import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { format as formatDate } from 'date-fns';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import { searchKeys } from '../keys';
import { downloadConversationsSearch, runConversationsPipeline, searchConversations } from './api';
import type { SearchConversationRequest } from './types';

export const useSearchConversationsQuery = (request: SearchConversationRequest) => {
  return useQuery({
    queryKey: searchKeys.query(request),
    queryFn: () => searchConversations(request),
    retry: false,
    placeholderData: keepPreviousData,
  });
};

type InfiniteParams = Omit<SearchConversationRequest, 'limit' | 'offset'> & { pageSize?: number };

export const useSearchConversationsInfiniteQuery = (params: InfiniteParams) => {
  const pageSize = params.pageSize ?? 50;

  return useInfiniteQuery({
    queryKey: searchKeys.infiniteQuery({ ...params, pageSize }),
    queryFn: ({ pageParam }) =>
      searchConversations({
        ...params,
        limit: pageSize,
        offset: (pageParam as number) * pageSize,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = ((lastPageParam as number) + 1) * pageSize;
      return lastPage.total > nextOffset ? lastPageParam + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
};

/**
 * Выгрузка найденных диалогов файлом: сохраняет пришедший файл на диск.
 * `isPending` держится всё время формирования файла — им блокируется повторный запуск.
 */
export const useDownloadConversationsSearch = () => {
  return useMutation({
    mutationFn: downloadConversationsSearch,
    onSuccess: (blob, { format }) => {
      // Имя повторяет то, которое бэкенд ставит в Content-Disposition: кросс-доменный ответ
      // этот заголовок браузеру не открывает, а из самого файла имя не восстановить.
      downloadFileFromBlob(blob, `dialogs_${formatDate(new Date(), 'yyyy-MM-dd')}.${format}`);
    },
  });
};

/**
 * Массовый запуск обработки диалогов текущей выборки.
 * `isPending` держится всё время запроса — им блокируется повторный запуск.
 */
export const useRunConversationsPipeline = () => {
  return useMutation({
    mutationFn: runConversationsPipeline,
  });
};
